import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, MessageSquareText, RotateCcw } from "lucide-react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import {
  acceptTask,
  type BoardTask,
  reopenTask,
  requestChanges,
  type RichDoc,
} from "@/lib/board";
import type { MoveAction, PendingMove } from "@/lib/moves";
import { qk } from "@/lib/queries";
import { handledClickupAuth } from "@/lib/reconnect";

import { RequestChangesDialog } from "./taskDetail/RequestChangesDialog";

// Accept is the one drag that reaches ClickUp (it posts the sign-off comment)
// and notifies the task's people, and it no longer asks first — so its toast
// holds the Undo well past sonner's 4s default.
const UNDO_MS = 10_000;

/**
 * Runs a drag-and-drop move. A failed mutation reverts the card to its source
 * column via `onCancel`; a settled one clears it via `onConfirmed`, called only
 * after the board refetch lands so there's no flicker.
 *
 * Accept and reopen fire the moment the card is dropped — dragging a card across
 * the board is deliberate enough on its own, so confirming it only got in the
 * way. Accept is the one with reach outside Greenlight, so its toast offers
 * `onUndo` instead: that hands a reopen back as an ordinary pending move, which
 * gets the same preview and board lock a dragged one would. A drag to "changes
 * requested" still opens the shared RequestChangesDialog (rich editor +
 * attachments) — not to confirm the move, but because the decision doesn't
 * exist without the feedback.
 */
export function MoveRunner({
  move,
  onCancel,
  onConfirmed,
  onUndo,
}: {
  move: PendingMove | null;
  onCancel: () => void;
  onConfirmed: (taskId: string, action: MoveAction) => void;
  onUndo: (task: BoardTask) => void;
}) {
  const queryClient = useQueryClient();

  // Reconcile the board, then clear the optimistic move. Awaiting the board
  // invalidation means the destination column already shows the real card by the
  // time the optimistic placeholder is removed. Identified by task + action, not
  // read off `move`, so settling an accept can't clear the reopen an Undo queued
  // behind it.
  async function settle(taskId: string, action: MoveAction) {
    await queryClient.invalidateQueries({ queryKey: qk.board });
    void queryClient.invalidateQueries({ queryKey: [...qk.task, taskId] });
    void queryClient.invalidateQueries({ queryKey: qk.notifications });
    void queryClient.invalidateQueries({ queryKey: qk.unread });
    onConfirmed(taskId, action);
  }

  const reopen = useMutation({
    mutationFn: (task: BoardTask) => reopenTask(task.id),
    onSuccess: async (_r, task) => {
      toast.success("Moved back to review", {
        icon: <RotateCcw className="size-4" />,
      });
      await settle(task.id, "reopen");
    },
    onError: (e) => {
      if (!handledClickupAuth(e)) toast.error("Couldn't move it back");
      onCancel();
    },
  });

  const accept = useMutation({
    mutationFn: (task: BoardTask) => acceptTask(task.id),
    onSuccess: async (_r, task) => {
      toast.success("Task accepted", {
        icon: <CircleCheck className="size-4" />,
        duration: UNDO_MS,
        action: { label: "Undo", onClick: () => onUndo(task) },
      });
      await settle(task.id, "accept");
    },
    onError: (e) => {
      if (!handledClickupAuth(e)) toast.error("Couldn't accept the task");
      onCancel();
    },
  });

  const reqChanges = useMutation({
    mutationFn: (vars: {
      task: BoardTask;
      feedback: string;
      rich: RichDoc | null;
      attachmentIds: string[];
    }) =>
      requestChanges(vars.task.id, vars.feedback, {
        rich: vars.rich,
        attachmentIds: vars.attachmentIds,
      }),
    onSuccess: async (_r, vars) => {
      toast.success("Changes requested", {
        icon: <MessageSquareText className="size-4" />,
      });
      await settle(vars.task.id, "request_changes");
    },
    onError: (e) => {
      if (!handledClickupAuth(e)) toast.error("Couldn't send the task back");
      onCancel();
    },
  });

  // Accept and reopen have no confirmation step, so the drop itself is the
  // trigger — as is an Undo, which arrives here as a reopen move. The ref keeps
  // a re-render (or StrictMode's double-invoke) from firing the same move twice;
  // clearing `move` (settled or reverted) resets it for the next one.
  const fired = useRef<string | null>(null);
  const acceptMutate = accept.mutate;
  const reopenMutate = reopen.mutate;
  useEffect(() => {
    if (!move || move.action === "request_changes") {
      fired.current = null;
      return;
    }
    const key = `${move.action}:${move.task.id}`;
    if (fired.current === key) return;
    fired.current = key;
    if (move.action === "accept") acceptMutate(move.task);
    else reopenMutate(move.task);
  }, [move, acceptMutate, reopenMutate]);

  // Changes requested — the shared rich form (editor + attachments).
  return (
    <RequestChangesDialog
      open={move?.action === "request_changes"}
      taskId={move?.task.id ?? null}
      taskTitle={move?.task.title ?? "this task"}
      submitting={reqChanges.isPending}
      onCancel={onCancel}
      onSubmit={(p) => {
        if (move) reqChanges.mutate({ task: move.task, ...p });
      }}
    />
  );
}

export default MoveRunner;
